import { create } from "zustand";
import { ApiError, get as apiGet, post as apiPost } from "@/api/client";
import {
	WorkbookCreateResponseSchema,
	WorkbookEventListSchema,
	WorkbookExportResponseSchema,
	WorkbookImportResponseSchema,
	WorkbookSnapshotListSchema,
	WorkbookSnapshotMutationResponseSchema,
	WorkbookSummaryListSchema,
	type WorkbookSections,
} from "@/api/schemas/workbook";
import {
	MOCK_WORKBOOK_RECORDS,
	defaultWorkbookSections,
	type WorkbookRecord,
	type WorkbookSectionsData,
	type WorkbookSnapshot,
	type WorkbookSnapshotData,
	type WorkbookSummary,
	toWorkbookSummary,
} from "@/features/workbook/mock-data";

export interface WorkbookSnapshotDraft {
	workbookName: string;
	propertyName: string;
	status: string;
	author: string;
	accessLevel: WorkbookSnapshotData["accessLevel"];
	description: string;
	sections: WorkbookSections;
}

export interface WorkbookSnapshotDiff {
	field: keyof WorkbookSnapshotData | keyof WorkbookSectionsData;
	from: string;
	to: string;
}

export interface WorkbookEvent {
	id: string;
	eventType: string;
	actor: string;
	occurredAt: string;
	snapshotId: string | null;
	payload: Record<string, unknown>;
}

const EMPTY_WORKBOOK_EVENTS: WorkbookEvent[] = [];

interface WorkbookState {
	records: WorkbookRecord[];
	eventsByWorkbook: Record<string, WorkbookEvent[]>;
	status: "idle" | "loading" | "ready" | "error";
	error: string | null;

	load: () => Promise<void>;
	listSummaries: () => WorkbookSummary[];
	getRecord: (workbookId: string) => WorkbookRecord | null;
	getSummary: (workbookId: string) => WorkbookSummary | null;
	getEvents: (workbookId: string) => WorkbookEvent[];
	loadEvents: (workbookId: string) => Promise<void>;
	importWorkbook: (params: {
		fileName: string;
		importKind: "workbook" | "handoff";
		propertyName?: string;
	}) => Promise<{ workbookId: string; taskId: string; } | null>;
	exportWorkbook: (
		workbookId: string,
		format: "xlsx" | "json",
	) => Promise<{ downloadUrl: string; taskId: string; } | null>;
	getSnapshot: (
		workbookId: string,
		snapshotId: string,
	) => WorkbookSnapshot | null;
	compareSnapshots: (
		workbookId: string,
		fromSnapshotId: string,
		toSnapshotId: string,
	) => WorkbookSnapshotDiff[];

	createDraft: (createdBy: string) => Promise<string>;
	saveSnapshot: (
		workbookId: string,
		draft: WorkbookSnapshotDraft,
		actor: string,
		reason?: string,
	) => Promise<string | null>;
	rollbackToSnapshot: (
		workbookId: string,
		snapshotId: string,
		actor: string,
	) => Promise<string | null>;
}

function cloneRecords(records: WorkbookRecord[]): WorkbookRecord[] {
	return records.map((record) => ({
		...record,
		snapshots: record.snapshots.map((snapshot) => ({
			...snapshot,
			data: { ...snapshot.data },
		})),
	}));
}


function diffSnapshotData(
	from: WorkbookSnapshotData,
	to: WorkbookSnapshotData,
): WorkbookSnapshotDiff[] {
	const fields: Array<keyof WorkbookSnapshotData> = [
		"workbookName",
		"propertyName",
		"status",
		"author",
		"accessLevel",
		"description",
	];

	return fields.flatMap((field) => {
		const fromVal = String(from[field]);
		const toVal = String(to[field]);
		return fromVal !== toVal ? [{ field, from: fromVal, to: toVal }] : [];
	});
}

function diffSections(
	from: WorkbookSectionsData,
	to: WorkbookSectionsData,
): WorkbookSnapshotDiff[] {
	const diffs: WorkbookSnapshotDiff[] = [];
	const push = (
		field: keyof WorkbookSectionsData,
		fromValue: string,
		toValue: string,
	) => {
		if (fromValue !== toValue) {
			diffs.push({ field, from: fromValue, to: toValue });
		}
	};

	push("hotelDescription", from.hotelDescription, to.hotelDescription);
	push("equipmentNotes", from.equipmentNotes, to.equipmentNotes);
	push("switchPortMapNotes", from.switchPortMapNotes, to.switchPortMapNotes);
	push("vlanLegendNotes", from.vlanLegendNotes, to.vlanLegendNotes);
	push(
		"equipmentList",
		JSON.stringify(from.equipmentList),
		JSON.stringify(to.equipmentList),
	);
	push(
		"switchPortMap",
		JSON.stringify(from.switchPortMap),
		JSON.stringify(to.switchPortMap),
	);
	push(
		"vlanLegend",
		JSON.stringify(from.vlanLegend),
		JSON.stringify(to.vlanLegend),
	);
	push(
		"isp",
		JSON.stringify(from.isp),
		JSON.stringify(to.isp),
	);

	return diffs;
}

function seedRecords(): WorkbookRecord[] {
	return cloneRecords(MOCK_WORKBOOK_RECORDS);
}

function normalizeAccessLevel(
	value: string,
): WorkbookSnapshotData["accessLevel"] {
	if (value === "Author" || value === "Read Write" || value === "Read Only") {
		return value;
	}
	if (value.toLowerCase() === "editor") {
		return "Read Write";
	}
	return "Author";
}

function fallbackEventsForRecord(record: WorkbookRecord): WorkbookEvent[] {
	return record.snapshots
		.slice()
		.reverse()
		.map((snapshot, index) => ({
			id: `${record.id}-evt-${snapshot.id}`,
			eventType: index === record.snapshots.length - 1 ? "workbook_created" : "snapshot_saved",
			actor: snapshot.createdBy,
			occurredAt: snapshot.createdAt,
			snapshotId: snapshot.id,
			payload: { reason: snapshot.reason },
		}));
}

function toApiErrorMessage(err: unknown): string {
	if (err instanceof ApiError) {
		return `API error ${err.status}`;
	}
	if (err instanceof Error) {
		return err.message;
	}
	return "unknown error";
}

function mapApiSnapshotsToRecord(
	workbookId: string,
	rows: ReturnType<typeof WorkbookSnapshotListSchema.parse>,
): WorkbookRecord {
	const snapshots = rows
		.slice()
		.reverse()
		.map<WorkbookSnapshot>((row) => ({
			id: row.id,
			createdAt: row.createdAt,
			createdBy: row.createdBy,
			reason: row.reason,
			data: {
				propertyName: row.data.propertyName,
				workbookName: row.data.workbookName,
				status: row.data.status,
				author: row.data.author,
				accessLevel: normalizeAccessLevel(row.data.accessLevel),
				description: row.data.description,
			},
			sections: {
				...defaultWorkbookSections(),
				...(row.data.sections ?? {}),
				isp: {
					...defaultWorkbookSections().isp,
					...((row.data.sections ?? {}).isp ?? {}),
					metadata: {
						...defaultWorkbookSections().isp.metadata,
						...((row.data.sections ?? {}).isp?.metadata ?? {}),
					},
					general: {
						...defaultWorkbookSections().isp.general,
						...((row.data.sections ?? {}).isp?.general ?? {}),
					},
					network: {
						...defaultWorkbookSections().isp.network,
						...((row.data.sections ?? {}).isp?.network ?? {}),
					},
					router: {
						...defaultWorkbookSections().isp.router,
						...((row.data.sections ?? {}).isp?.router ?? {}),
					},
				},
			},
		}));

	const active =
		snapshots.find((item) => rows.some((row) => row.id === item.id && row.isActive)) ??
		snapshots[snapshots.length - 1];

	return {
		id: workbookId,
		activeSnapshotId: active?.id ?? "",
		snapshots,
	};
}

async function fetchRecord(workbookId: string): Promise<WorkbookRecord> {
	const raw = await apiGet<unknown>(`/workbook/${workbookId}/snapshots/`);
	const snapshots = WorkbookSnapshotListSchema.parse(raw);
	return mapApiSnapshotsToRecord(workbookId, snapshots);
}

export const useWorkbookStore = create<WorkbookState>()(
	(set, get) => ({
		records: seedRecords(),
		eventsByWorkbook: {},
		status: "idle",
		error: null,

		load: async () => {
			if (get().status === "loading") {
				return;
			}

			set({ status: "loading", error: null });
			try {
				const raw = await apiGet<unknown>("/workbook/");
				const summaries = WorkbookSummaryListSchema.parse(raw);
				const records = await Promise.all(
					summaries.map(async (summary) => fetchRecord(summary.id)),
				);
				set({ records, status: "ready", error: null });
			} catch (err) {
				const message = toApiErrorMessage(err);
				set({
					records: seedRecords(),
					status: "error",
					error: `${message} — showing fixture data`,
				});
			}
		},

		listSummaries: () => get().records.map(toWorkbookSummary),

		getRecord: (workbookId) =>
			get().records.find((record) => record.id === workbookId) ?? null,

		getSummary: (workbookId) => {
			const record = get().records.find((item) => item.id === workbookId);
			return record ? toWorkbookSummary(record) : null;
		},

		getEvents: (workbookId) =>
			get().eventsByWorkbook[workbookId] ?? EMPTY_WORKBOOK_EVENTS,

		loadEvents: async (workbookId) => {
			try {
				const raw = await apiGet<unknown>(`/workbook/${workbookId}/events/`);
				const rows = WorkbookEventListSchema.parse(raw);
				const events: WorkbookEvent[] = rows.map((row) => ({
					id: row.id,
					eventType: row.eventType,
					actor: row.actor,
					occurredAt: row.occurredAt,
					snapshotId: row.snapshotId ?? null,
					payload: row.payload ?? {},
				}));
				set((state) => ({
					eventsByWorkbook: {
						...state.eventsByWorkbook,
						[workbookId]: events,
					},
				}));
			} catch {
				const record = get().records.find((item) => item.id === workbookId);
				if (!record) {
					return;
				}
				set((state) => ({
					eventsByWorkbook: {
						...state.eventsByWorkbook,
						[workbookId]: fallbackEventsForRecord(record),
					},
				}));
			}
		},

		importWorkbook: async (params) => {
			try {
				const raw = await apiPost<unknown>("/workbook/import/", {
					fileName: params.fileName,
					importKind: params.importKind,
					propertyName: params.propertyName ?? "Imported Property",
				});
				const parsed = WorkbookImportResponseSchema.parse(raw);
				const record = await fetchRecord(parsed.workbookId);
				set((state) => {
					const withoutCurrent = state.records.filter(
						(item) => item.id !== parsed.workbookId,
					);
					return {
						records: [record, ...withoutCurrent],
						eventsByWorkbook: {
							...state.eventsByWorkbook,
							[parsed.workbookId]: fallbackEventsForRecord(record),
						},
						status: "ready",
						error: null,
					};
				});
				return { workbookId: parsed.workbookId, taskId: parsed.taskId };
			} catch {
				return null;
			}
		},

		exportWorkbook: async (workbookId, format) => {
			try {
				const raw = await apiPost<unknown>(`/workbook/${workbookId}/export/`, {
					format,
				});
				const parsed = WorkbookExportResponseSchema.parse(raw);
				return { downloadUrl: parsed.downloadUrl, taskId: parsed.taskId };
			} catch {
				return null;
			}
		},

		getSnapshot: (workbookId, snapshotId) => {
			const record = get().records.find((item) => item.id === workbookId);
			if (!record) {
				return null;
			}
			return (
				record.snapshots.find((snapshot) => snapshot.id === snapshotId) ?? null
			);
		},

		compareSnapshots: (workbookId, fromSnapshotId, toSnapshotId) => {
			const record = get().records.find((item) => item.id === workbookId);
			if (!record) {
				return [];
			}

			const fromSnapshot = record.snapshots.find(
				(snapshot) => snapshot.id === fromSnapshotId,
			);
			const toSnapshot = record.snapshots.find(
				(snapshot) => snapshot.id === toSnapshotId,
			);

			if (!fromSnapshot || !toSnapshot) {
				return [];
			}

			return [
				...diffSnapshotData(fromSnapshot.data, toSnapshot.data),
				...diffSections(fromSnapshot.sections, toSnapshot.sections),
			];
		},

		createDraft: async (createdBy) => {
			const raw = await apiPost<unknown>("/workbook/", {
				workbookName: "New Workbook",
				propertyName: "New Property",
				status: "Draft",
				author: createdBy,
				accessLevel: "Author",
				description:
					"New workbook draft. Update metadata and save to create additional revisions.",
				reason: "Workbook created",
				sections: defaultWorkbookSections(),
			});
			const parsed = WorkbookCreateResponseSchema.parse(raw);
			const record = await fetchRecord(parsed.id);
			set((state) => {
				const withoutCurrent = state.records.filter((item) => item.id !== parsed.id);
				return {
					records: [record, ...withoutCurrent],
					eventsByWorkbook: {
						...state.eventsByWorkbook,
						[parsed.id]: fallbackEventsForRecord(record),
					},
					status: "ready",
					error: null,
				};
			});
			return parsed.id;
		},

		saveSnapshot: async (workbookId, draft, _actor, reason) => {
			try {
				const raw = await apiPost<unknown>(`/workbook/${workbookId}/snapshots/`, {
					workbookName: draft.workbookName,
					propertyName: draft.propertyName,
					status: draft.status,
					author: draft.author,
					accessLevel: draft.accessLevel,
					description: draft.description,
					reason: reason ?? "Metadata updated",
					sections: draft.sections,
				});
				const parsed = WorkbookSnapshotMutationResponseSchema.parse(raw);
				const updatedRecord = await fetchRecord(workbookId);
				set((state) => {
					const records = state.records.map((item) =>
						item.id === workbookId ? updatedRecord : item,
					);
					return {
						records,
						eventsByWorkbook: {
							...state.eventsByWorkbook,
							[workbookId]: fallbackEventsForRecord(updatedRecord),
						},
						status: "ready",
						error: null,
					};
				});
				return parsed.snapshotId;
			} catch {
				return null;
			}
		},

		rollbackToSnapshot: async (workbookId, snapshotId, _actor) => {
			try {
				const raw = await apiPost<unknown>(`/workbook/${workbookId}/rollback/`, {
					targetSnapshotId: snapshotId,
				});
				const parsed = WorkbookSnapshotMutationResponseSchema.parse(raw);
				const updatedRecord = await fetchRecord(workbookId);
				set((state) => {
					const records = state.records.map((item) =>
						item.id === workbookId ? updatedRecord : item,
					);
					return {
						records,
						eventsByWorkbook: {
							...state.eventsByWorkbook,
							[workbookId]: fallbackEventsForRecord(updatedRecord),
						},
						status: "ready",
						error: null,
					};
				});
				return parsed.snapshotId;
			} catch {
				return null;
			}
		},
	}),
);
